import { z } from 'zod';
import { httpUrlSchema } from './common.js';
/**
 * Validation des clés critiques d'une configuration Ghost.
 *
 * Volontairement permissive (`passthrough`) : Ghost accepte de nombreuses clés que Vigie n'a
 * pas à connaître, et un schéma fermé rejetterait des configurations parfaitement valides.
 * On ne valide que ce dont une erreur casse le site au redémarrage.
 */
/** Transports mail reconnus par Ghost (nodemailer). */
export const MAIL_TRANSPORTS = ['SMTP', 'Sendmail', 'Direct'];
/** Clients de base de données reconnus par Ghost (knex). */
export const DATABASE_CLIENTS = ['mysql2', 'mysql', 'sqlite3'];
const portSchema = z.number().int().min(1).max(65535);
export const ghostServerSchema = z
    .object({
    host: z.string().min(1).optional(),
    port: portSchema.optional(),
})
    .passthrough();
export const ghostDatabaseSchema = z
    .object({
    client: z.enum(DATABASE_CLIENTS),
    connection: z
        .object({
        host: z.string().min(1).optional(),
        port: portSchema.optional(),
        user: z.string().optional(),
        password: z.string().optional(),
        database: z.string().optional(),
        filename: z.string().optional(),
    })
        .passthrough(),
})
    .passthrough()
    .superRefine((value, ctx) => {
    if (value.client === 'sqlite3') {
        if (!value.connection.filename) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ['connection', 'filename'],
                message: 'database.connection.filename est requis avec le client sqlite3',
            });
        }
        return;
    }
    if (!value.connection.database) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['connection', 'database'],
            message: 'database.connection.database est requis avec un client MySQL',
        });
    }
});
export const ghostMailSchema = z
    .object({
    transport: z.enum(MAIL_TRANSPORTS),
    from: z.string().min(1).optional(),
    options: z
        .object({
        host: z.string().min(1).optional(),
        port: portSchema.optional(),
        secure: z.boolean().optional(),
        service: z.string().optional(),
        auth: z
            .object({
            user: z.string().optional(),
            pass: z.string().optional(),
        })
            .passthrough()
            .optional(),
    })
        .passthrough()
        .optional(),
})
    .passthrough()
    .superRefine((value, ctx) => {
    if (value.transport !== 'SMTP')
        return;
    const options = value.options;
    if (!options?.host && !options?.service) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ['options', 'host'],
            message: 'Le transport SMTP requiert mail.options.host (ou mail.options.service)',
        });
    }
});
/**
 * Envoi en masse : Ghost n'a d'implémentation que pour Mailgun.
 *
 * Valider la forme évite le piège le plus courant — une base d'API sans schéma, ou la région
 * américaine pour un compte européen, qui donne un « domain not found » sur un domaine qui existe.
 */
export const ghostBulkEmailSchema = z
    .object({
    mailgun: z
        .object({
        apiKey: z.string().min(1).optional(),
        domain: z.string().min(1).optional(),
        baseUrl: httpUrlSchema.optional(),
        tag: z.string().optional(),
    })
        .passthrough()
        .optional(),
    batchSize: z.number().int().min(1).max(1000).optional(),
})
    .passthrough();
/**
 * Statistiques de trafic (Ghost 6).
 *
 * Les points d'accès sont validés comme des URL : une valeur sans schéma est acceptée par Ghost et
 * désactive silencieusement les statistiques, ce qui est exactement le genre de panne qu'on ne
 * trouve jamais. Le reste reste ouvert — la forme évolue vite d'une version à l'autre.
 */
export const ghostTinybirdSchema = z
    .object({
    workspaceId: z.string().min(1).optional(),
    adminToken: z.string().min(1).optional(),
    tracker: z
        .object({
        endpoint: httpUrlSchema.optional(),
        token: z.string().optional(),
        datasource: z.string().optional(),
        local: z
            .object({
            enabled: z.boolean().optional(),
            endpoint: httpUrlSchema.optional(),
            token: z.string().optional(),
            datasource: z.string().optional(),
        })
            .passthrough()
            .optional(),
    })
        .passthrough()
        .optional(),
    stats: z
        .object({
        endpoint: httpUrlSchema.optional(),
        token: z.string().optional(),
        id: z.string().optional(),
        local: z
            .object({
            enabled: z.boolean().optional(),
            endpoint: httpUrlSchema.optional(),
            token: z.string().optional(),
        })
            .passthrough()
            .optional(),
    })
        .passthrough()
        .optional(),
})
    .passthrough();
export const ghostSecuritySchema = z
    .object({
    staffDeviceVerification: z.boolean().optional(),
    allowWebhookInternalIPs: z.boolean().optional(),
})
    .passthrough();
export const ghostConfigSchema = z
    .object({
    url: httpUrlSchema,
    server: ghostServerSchema.optional(),
    database: ghostDatabaseSchema.optional(),
    mail: ghostMailSchema.optional(),
    bulkEmail: ghostBulkEmailSchema.optional(),
    tinybird: ghostTinybirdSchema.optional(),
    security: ghostSecuritySchema.optional(),
    imageOptimization: z
        .object({ resize: z.boolean().optional(), srcsets: z.boolean().optional() })
        .passthrough()
        .optional(),
    logging: z
        .object({
        level: z.enum(['error', 'warn', 'info', 'debug']).optional(),
        rotation: z
            .object({
            enabled: z.boolean().optional(),
            period: z.string().optional(),
            count: z.number().int().min(1).optional(),
        })
            .passthrough()
            .optional(),
    })
        .passthrough()
        .optional(),
    privacy: z
        .union([z.literal(false), z.object({ useGravatar: z.boolean().optional() }).passthrough()])
        .optional(),
})
    .passthrough();
/**
 * Valide une configuration Ghost déjà désérialisée et renvoie des messages en français
 * exploitables directement par l'interface (pas d'exception : l'appelant affiche la liste).
 */
export function validateGhostConfig(value) {
    const parsed = ghostConfigSchema.safeParse(value);
    if (parsed.success)
        return { valid: true, issues: [] };
    return {
        valid: false,
        issues: parsed.error.issues.map((issue) => ({
            path: issue.path.join('.'),
            message: issue.message,
        })),
    };
}
//# sourceMappingURL=ghost-config.js.map